import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Send, Loader2, RotateCw } from 'lucide-react';
import { AgentSseService } from '@/src/workplace/services/agent-sse-service';
import type { AgentResult } from '@/src/workplace/services/agent-sse-service';
import { updateWorkspaceText } from '@/lib/api/filesystem';
import { createLogger } from '@/src/utils/logger';
import { ChatMarkdown } from '@/src/workplace/chat/components/chat-markdown';
import { buildClaudeMd } from './ai-ta-claude-md';
import { DEFAULT_AI_TA_WELCOME } from './ai-ta-prompt';
import './ai-ta-panel.less';

const log = createLogger('AiTaPanel');

interface AiTaMessage {
	role: 'user' | 'assistant' | 'system';
	content: string;
	isStreaming?: boolean;
}

interface AiTaPanelProps {
	system?: string;
	ai?: string;
	goal?: string;
	criteria?: string;
	courseId: string;
	chapterId: string;
	// 课程上下文，用于会话管理和 system prompt 构建
	courseTitle?: string;
	chapterTitle?: string;
	chapterIntro?: string;
	chapterObjective?: string;
}

/**
 * 课程页 AI 助教面板
 *
 * 复用 workplace 的 AgentSseService
 * 每次打开都是新会话，不持久化历史
 * 创建实例时注入课程上下文到 CLAUDE.md
 */
export function AiTaPanel({
	system = '',
	ai = DEFAULT_AI_TA_WELCOME,
	goal,
	criteria,
	courseId: _courseId,
	chapterId: _chapterId,
	courseTitle,
	chapterTitle,
	chapterIntro,
	chapterObjective,
}: AiTaPanelProps) {
	const [messages, setMessages] = useState<AiTaMessage[]>([{ role: 'assistant', content: ai }]);
	const [input, setInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [initializing, setInitializing] = useState(false);

	const sseServiceRef = useRef<AgentSseService | null>(null);
	const pendingMessageRef = useRef<{ id: string; content: string } | null>(null);
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const initLockRef = useRef(false);
	const [resetConfirming, setResetConfirming] = useState(false);
	const resetTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

	// 初始化 SSE 服务
	const sseService = useMemo(() => {
		if (!sseServiceRef.current) {
			sseServiceRef.current = new AgentSseService();
		}
		return sseServiceRef.current;
	}, []);

	// 构建完整的 CLAUDE.md 内容
	const claudeMdContent = useMemo(
		() =>
			buildClaudeMd({
				courseTitle,
				chapterTitle,
				chapterIntro,
				chapterObjective,
				goal,
				criteria,
				system,
			}),
		[system, goal, criteria, courseTitle, chapterTitle, chapterIntro, chapterObjective],
	);

	// 消息变化时，在面板内部滚动到底部，不影响外层页面
	useEffect(() => {
		const el = messagesContainerRef.current;
		if (el) {
			el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
		}
	}, [messages]);

	// 初始化 AI 会话（点击按钮时调用）
	const initSession = useCallback(async () => {
		const instanceId = sseService.getInstanceId();
		if (instanceId) {
			log.info('实例已存在', { instanceId });
			return true;
		}

		// 防止 React Strict Mode 双重渲染导致并发 allocate
		if (initLockRef.current) {
			log.info('初始化进行中，跳过重复调用');
			return false;
		}
		initLockRef.current = true;

		setInitializing(true);
		try {
			// 创建新实例
			log.info('开始创建 AI 工作区');
			await sseService.allocate();
			const newInstanceId = sseService.getInstanceId();
			log.info('工作区创建成功', { instanceId: newInstanceId });

			// 注入课程上下文到 CLAUDE.md
			if (newInstanceId && claudeMdContent) {
				log.info('开始注入课程提示词到 CLAUDE.md', {
					contentLength: claudeMdContent.length,
					hasGoal: !!goal,
					hasCriteria: !!criteria,
				});
				log.debug('CLAUDE.md 内容', claudeMdContent);
				try {
					await updateWorkspaceText(newInstanceId, { 'CLAUDE.md': claudeMdContent });
					log.info('课程提示词注入成功', { instanceId: newInstanceId });
				} catch (err) {
					log.error('课程提示词注入失败', err);
				}
			}

			// 启动 SSE 监听
			sseService.startListening(
				// onMessage: 收到文本消息
				(content: string) => {
					log.debug('收到 SSE 消息', { contentLength: content.length });
					const pending = pendingMessageRef.current;
					if (pending) {
						// 追加到当前流式消息
						setMessages((prev) => prev.map((msg) => (msg.isStreaming ? { ...msg, content: msg.content + content } : msg)));
					} else {
						// 新消息（可能是历史或其他情况）
						setMessages((prev) => [...prev, { role: 'assistant', content }]);
					}
				},
				{
					// onResult: 查询结束，收到最终结果
					onResult: (result: AgentResult) => {
						log.info('查询完成', { duration: result.duration_ms, stopReason: result.stop_reason });
						setLoading(false);
						// 移除流式状态
						setMessages((prev) => prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg)));
						pendingMessageRef.current = null;
					},
					// onEnd: SSE 流结束
					onEnd: () => {
						log.debug('SSE 流结束');
						setLoading(false);
						pendingMessageRef.current = null;
					},
					// onError: 错误处理
					onError: (error) => {
						log.error('SSE 错误', error.message);
						setLoading(false);
						setMessages((prev) => [...prev, { role: 'system', content: '连接失败，请稍后重试' }]);
						pendingMessageRef.current = null;
					},
				},
			);

			return true;
		} catch (err) {
			log.error('初始化失败', err);
			setMessages((prev) => [...prev, { role: 'system', content: '连接失败，请稍后重试' }]);
			return false;
		} finally {
			initLockRef.current = false;
			setInitializing(false);
		}
	}, [sseService, claudeMdContent, goal, criteria]);

	// 挂载时自动初始化会话
	useEffect(() => {
		initSession();
	}, [initSession]);

	// 发送消息
	const handleSend = useCallback(async () => {
		const trimmed = input.trim();
		if (!trimmed || loading) return;

		const instanceId = sseService.getInstanceId();
		if (!instanceId) {
			log.warn('发送消息失败：实例不存在');
			setMessages((prev) => [...prev, { role: 'system', content: '未连接，请重新打开' }]);
			return;
		}

		log.info('发送用户消息', { messageLength: trimmed.length });
		// 添加用户消息
		setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
		setInput('');
		setLoading(true);

		// 创建流式消息占位
		pendingMessageRef.current = { id: `msg-${Date.now()}`, content: '' };
		setMessages((prev) => [...prev, { role: 'assistant', content: '', isStreaming: true }]);

		try {
			// 每次发送前重新写入 CLAUDE.md，确保 agent session 读取最新提示词
			try {
				await updateWorkspaceText(instanceId, { 'CLAUDE.md': claudeMdContent });
			} catch {
				/* 写入失败不阻塞发送 */
			}
			await sseService.send(trimmed);
			log.info('消息发送成功');
		} catch (err) {
			log.error('发送消息失败', err);
			setMessages((prev) => prev.filter((msg) => !msg.isStreaming));
			setLoading(false);
			pendingMessageRef.current = null;
		}
	}, [input, loading, sseService, claudeMdContent]);

	const handleReset = useCallback(async () => {
		if (resetConfirming) {
			if (resetTimerRef.current) {
				clearTimeout(resetTimerRef.current);
				resetTimerRef.current = null;
			}
			setResetConfirming(false);

			log.info('用户重置 AI 助教会话');
			initLockRef.current = false;
			sseService.cleanup();
			setMessages([{ role: 'assistant', content: ai }]);
			setInput('');
			setLoading(false);
			await initSession();
			return;
		}

		setResetConfirming(true);
		resetTimerRef.current = setTimeout(() => {
			setResetConfirming(false);
			resetTimerRef.current = null;
		}, 3000);
	}, [resetConfirming, sseService, ai, initSession]);

	// 清理 SSE 连接
	useEffect(() => {
		return () => {
			log.debug('清理 SSE 连接');
			sseService.stopListening();
			if (resetTimerRef.current) {
				clearTimeout(resetTimerRef.current);
			}
		};
	}, [sseService]);

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
			e.preventDefault();
			handleSend();
		}
	};

	return (
		<div className="ai-ta-panel ai-ta-panel--open">
			<div className="ai-ta-panel__header">
				<div className="ai-ta-panel__header-title">
					<span className="ai-ta-panel__header-badge">
						<span className="ai-ta-panel__header-badge-text">AI</span>
					</span>
					<span>AI 助教</span>
				</div>
				<button
					className={`ai-ta-panel__reset${resetConfirming ? ' ai-ta-panel__reset--confirming' : ''}`}
					disabled={initializing}
					onClick={handleReset}
					type="button"
				>
					<RotateCw style={{ width: 12, height: 12 }} />
					<span>{resetConfirming ? '确认重置？' : '重置'}</span>
				</button>
			</div>

			<div className="ai-ta-panel__messages" ref={messagesContainerRef}>
				{messages.map((msg, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: messages are append-only, never reordered
					<div className={`ai-ta-panel__message ai-ta-panel__message--${msg.role}`} key={i}>
						{msg.role === 'assistant' ? (
							msg.content ? (
								<ChatMarkdown content={msg.content} />
							) : msg.isStreaming ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : null
						) : (
							msg.content
						)}
					</div>
				))}
			</div>

			<div className="ai-ta-panel__input-area">
				<div className="ai-ta-panel__input-wrapper">
					<input
						className="ai-ta-panel__input"
						disabled={loading}
						onChange={(e) => setInput(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="输入你的问题..."
						type="text"
						value={input}
					/>
					<button className="ai-ta-panel__send" disabled={!input.trim() || loading} onClick={handleSend} type="button">
						{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
					</button>
				</div>
			</div>
		</div>
	);
}
